import { useEffect, useMemo } from 'react';
import { useState } from 'owa-react';
import type { OwaHeaderButtonControl } from '../types/OwaHeaderButtonControl';
import { headerButtons } from '../components/buttons/headerButtons';

interface AsyncVisibilityState {
    [key: string]: boolean;
}

export default function useHeaderButtonVisibility(): OwaHeaderButtonControl[] {
    const [asyncVisibilityMap, setAsyncVisibilityMap] =
        /* eslint-disable-next-line owa-custom-rules/prefer-react-state-without-arrays-or-objects -- (https://aka.ms/OWALintWiki)
         * Using a local React state object is preferred over Satchel store for this component-specific visibility tracking.
         * Rationale:
         * 1. Component-scoped state: Button visibility is only relevant to this component and doesn't need global sharing
         * 2. Encapsulation: Keeps the complexity of async visibility resolution contained within this hook
         * 3. Simplification: Eliminates the need for Satchel mutators, orchestrators, and selectors for this local concern
         * 4. Multiple primitives would be impractical: Having separate useState for each dynamic button would create
         *    an unpredictable number of state variables and make the code harder to maintain
         * This object represents a cohesive unit of related boolean states that belong together. */
        useState<AsyncVisibilityState>({});

    const visibilityResults = useMemo(() => {
        const results: Record<string, boolean | Promise<boolean>> = {};

        for (const button of headerButtons) {
            if (!button.isVisible) {
                // No visibility function means always visible
                results[button.id] = true;
            } else {
                try {
                    results[button.id] = button.isVisible();
                } catch {
                    results[button.id] = false;
                }
            }
        }

        return results;
    }, []);

    useEffect(() => {
        for (const button of headerButtons) {
            const result = visibilityResults[button.id];

            if (result instanceof Promise) {
                result.then(isVisible => {
                    setAsyncVisibilityMap(prevState => ({
                        ...prevState,
                        [button.id]: isVisible,
                    }));
                });
            }
        }
    }, [visibilityResults]);

    return useMemo(() => {
        return headerButtons.filter(button => {
            const result = visibilityResults[button.id];

            if (result instanceof Promise) {
                return asyncVisibilityMap[button.id] ?? false;
            } else {
                return result;
            }
        });
    }, [visibilityResults, asyncVisibilityMap]);
}
